import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Image, Users, Palette,
  FileText, Mail, LogOut, Newspaper, Send,
  Menu, X, ChevronRight, Inbox, AlertTriangle, Camera, Calendar
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: FileText, label: 'Pages', path: '/admin/pages' },
    { icon: Image, label: 'Media Library', path: '/admin/media' },
    { icon: Palette, label: 'Artworks', path: '/admin/artworks' },
    { icon: AlertTriangle, label: 'Missing Artworks', path: '/admin/missing-artworks' },
    { icon: Users, label: 'Artists', path: '/admin/artists' },
    { icon: Newspaper, label: 'News & Press', path: '/admin/news' },
    { icon: Calendar, label: 'Exhibitions', path: '/admin/exhibitions' },
    { icon: Mail, label: 'Contact Forms', path: '/admin/contacts' },
    { icon: Camera, label: 'Consultations', path: '/admin/consultations' },
    { icon: Inbox, label: 'Consignments', path: '/admin/consignments' },
    { icon: Mail, label: 'Mailing List', path: '/admin/mailing-list' },
    { icon: Send, label: 'Campaigns', path: '/admin/campaigns' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            className="w-64 bg-black text-white fixed h-full z-50"
          >
            {/* Logo */}
            <div className="p-6 border-b border-gray-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold">Z1</span>
                </div>
                <div>
                  <h1 className="font-semibold">Zebra One</h1>
                  <p className="text-xs text-gray-400">Est. 1976</p>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <nav className="p-4 space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all group relative ${
                      isActive 
                        ? 'bg-white text-black' 
                        : 'hover:bg-gray-800 text-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute right-0 top-0 bottom-0 w-1 bg-white rounded-l"
                      />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* User Section */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{user?.name || 'Admin'}</p>
                  <p className="text-xs text-gray-400">{user?.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${
        sidebarOpen ? 'ml-64' : 'ml-0'
      }`}>
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            <div className="flex items-center gap-4">
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-600 hover:text-black flex items-center gap-1"
              >
                View Site
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="min-h-[calc(100vh-73px)]">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;